import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { getQueryClient, theme } from '@cliengo/mfe-utils/mod';
import Navbar from './components/Navbar';
import { WhatsAppIntegration } from './components/WhatsAppIntegration';
import InvalidWebsiteState from './components/InvalidWebsiteState';
import LoadingState from './components/LoadingState';
import { WhatsAppCloudOnboarding } from './components/WhatsAppCloudOnboarding';
import { useCurrentWebsite } from './hooks/useCurrentWebsite';
import { useWebsiteId } from './hooks/useWebsiteId';
import { useQueryParams } from './hooks/useQueryParams';
import { useWhatsAppCloudFlag } from './hooks/useWhatsAppCloudFlag';
import { WHATSAPP_CLOUD_MODE_PARAM } from './constants/whatsapp-cloud';

export interface AppProps {
  initialPath?: string;
  routerStrategy?: 'browser' | 'memory';
  refresh?: () => void;
}

function AppContent() {
  const websiteId = useWebsiteId();
  const { website, isLoading, error, isValid } = useCurrentWebsite();
  // `?mode=cloud` sólo cuenta con el flag prendido; si no, flujo Coexistence de siempre.
  const { mode } = useQueryParams();
  const wantsCloud = mode === WHATSAPP_CLOUD_MODE_PARAM;
  const cloudFlag = useWhatsAppCloudFlag(wantsCloud);

  // If no websiteId is provided in query params
  if (!websiteId) {
    return <InvalidWebsiteState error="No se proporcionó un ID de sitio web válido." />;
  }

  // Show loading state while fetching website data
  if (isLoading || cloudFlag.isLoading) {
    return <LoadingState />;
  }

  // Show error state if website is invalid or there's an error
  if (error || !isValid) {
    return <InvalidWebsiteState error={error?.message} />;
  }

  return (
    <div className="App">
      <Navbar />
      <div className="pt-[50px]">
        {wantsCloud && cloudFlag.isOn ? <WhatsAppCloudOnboarding /> : <WhatsAppIntegration />}
      </div>
    </div>
  );
}

export function App(props: AppProps) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={getQueryClient()}>
        <AppContent />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
